import { Params } from '@angular/router';
import { LocationData } from '@openapi/model/location-data';
import { MessagingRunObject } from '../epn-signup/components/messaging-run/messaging-run-object';
import { DropdownValueObject } from './ui-components/dropdown/dropdown-value-object';
import { MultiMessageConfig } from './domain-objects/company/integration/epn-integration/multi-message-config';
import { TimeBeforeTypeEnum } from './domain-objects/company/integration/epn-integration/time-before-type.enum';
import { DayOfTypeEnum } from './domain-objects/company/integration/epn-integration/day-of-type.enum';
import { WeekOfTypeEnum } from './domain-objects/company/integration/epn-integration/week-of-type.enum';
import { PeSignupState } from '../dashboard/dashboard/pe-type.enum';
import { ContentGroupTypeEnum } from './domain-objects/messaging-configuration/content-group-type.enum';
import { BusinessRegistrationInfo } from '@openapi/model/business-registration-info';

import {
  ConnSortColumn,
  ConnSortDirection,
} from '../eweb-signup/sortable-header.directive';
import {
  AdminOffice,
  CompanySearchRequest,
  EpmReasonForVisit,
  EwxReasonForVisitCommand,
  PeFeatureToggleDto,
  VerificationContactInfo,
} from '@openapi';
import { EwxProviderType } from './domain-objects/company/integration/ewx-provider-type.enum';
import { HourToRunEnum } from './domain-objects/company/integration/epn-integration/hour-to-run.enum';
import { NgxMaskConfig } from 'ngx-mask';

/** Validates complete email address format (RFC 5322 compliant) */
export const EMAIL_VALIDATION_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/** Restricts email input to common characters: alphanumeric, period, @, underscore, hyphen, plus */
export const EMAIL_INPUT_ALLOWED_CHARACTERS_REGEX = /[a-zA-Z0-9.@_+-]/;
/** Matches Freemarker template variables in message content (e.g., ${reminder.patientFirstName}) */
export const MESSAGE_CONTENT_REPLACEMENT_REGEX =
  /[${].+?(?=,|\s|\.\s|\.\n|\.$|$)/g;

/**
 * https://stackoverflow.com/questions/10306690/what-is-a-regular-expression-which-will-match-a-valid-domain-name-without-a-subd
 * @type {RegExp}
 */
export const DOMAIN_VALIDATION_REGEX =
  /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
export const EFAN_LENGTH = 10;
export const REMOVE_NON_NUMBERS_FROM_PHONE_NUMBER = /(\(|\)|_|-|\W)/g;

export const CLEAN_UP_PHONE_REGEX = /\+1/;

export const EPM_CLIENT_ID = 'epm-peconn-2-ui';
export const EYEFINITY_CLIENT_ID = 'eye-peconn-2-ui';

export const OLD_EPM_CLIENT_ID = 'epm-peconn-ui';
export const OLD_EYEFINITY_CLIENT_ID = 'eye-peconn-ui';

export const ENCOMPASS_CLIENT_ID = 'encompass-peconn-2-ui';

export enum AppEnvironmentName {
  DEV = 'dev',
  STG = 'staging',
  PROD = 'prod',
  SBOX = 'sandbox',
}

// paths for use by the Router object
export const EPM_URL = '/epm';
export const ENCOMPASS_URL = '/encompass';
export const EYEFINITY_URL = '/eyefinity';
export const DASHBOARD_URL = '/dashboard';
export const SUPPORT_URL = '/support';

export const DASHBOARD_ERROR_URL = '/dashboard-error';
export const SIGNUP_URL = '/signup';
export const PENDING_URL = '/pending';
export const MANAGEMENT_URL = '/manage';
export const EPN_URL = '/epn';
export const EWX_URL = '/ewx';
export const REASON_FOR_VISIT_URL = '/reason-for-visit';
export const MESSAGE_CONFIG_URL = '/message-configuration';
export const FEATURE_TOGGLE_URL = '/feature-toggle';

export const REFRESHER = 'refresher';

export const APPLICATION_NAMES = {
  eyefinity: 'eyefinity',
  epm: 'epm',
  pe: 'pe',
  encompass: 'encompass',
};

/** Authentication Interceptor
 */
export const MAX_NUMBER_OF_RETRIES = 2;

export const INTEGRATION_ERROR = 'expired refresh token';
export const AUTHENTICATION_ERROR = 401;
export const FORBIDDEN_ERROR = 403;
export const SERVER_ERROR = 500;

// Routes for use by the routing modules
export const ROUTES_PE_EMPLOYEE = 'pe';
export const ROUTES_EPM = 'epm';
export const ROUTES_ENCOMPASS = 'encompass';
export const ROUTES_DASHBOARD_ERROR = 'dashboard-error';

export const ROUTES_EPN = 'epn';
export const ROUTES_EWX = 'ewx';
export const ROUTES_ENCOMPASS_FORMS = 'encompass-forms';

export const ROUTES_SUPPORT = 'support';
export const ROUTES_DASHBOARD = 'dashboard';
export const ROUTES_SIGNUP = 'signup';
export const ROUTES_PENDING = 'pending';
export const ROUTES_MESSAGE_CONFIG = 'message-configuration';
export const ROUTES_MANAGEMENT = 'manage';
export const ROUTES_REASON_FOR_VISIT = 'reason-for-visit';
export const ROUTES_FEATURE_TOGGLE = 'feature-toggle';
export const ROUTES_EDIT_EFAN_DOMAIN = 'edit-efan-domain';
export const ROUTES_MANAGE_EOS_LINKS = 'eos-links';

export const ROUTES_EYEFINITY = 'eyefinity';

export const INVALID_STATUS = 'INVALID STATUS';
export const INVALID_TYPE = 'INVALID TYPE';

export const API_REFRESH = '/refresh';
export const API_EPN_SIGNUP = '/signupEPN';
export const API_EWEB_CANCELLATION = '/cancelEwx';
export const API_EPN_CANCELLATION = '/cancelEpn';
export const API_EWEB_MANAGE = '/manageEwx';
export const API_EWEB_MANAGE_MY_SITE = '/manageEwxWebsite';
export const API_MESSAGING_RUN = '/messagingRun';
export const API_MESSAGES = '/epnMessages';
export const API_MESSAGE_REPLIES = '/epnMessageReplies';
export const API_SIGNUP_STATUS = '/signupStatus';
export const API_LOGIN = '/login';
export const API_MESSAGE_CONFIG_CONTENT =
  '/messageConfigContent?messageConfigTypeId=';
export const API_MANAGE_MESSAGE_CONFIG_CONTENT = '/manageSelectedContent';
export const API_REASON_FOR_VISIT = '/reasonForVisit';
export const API_MANAGE_FEATURE_TOGGLE = '/manageFeatureToggle';
export const API_MANAGE_REASON_FOR_VISIT = '/manageReasonForVisit';
export const API_CONFIGURE_EPM_INTEGRATION = '/configureEpmIntegration';
export const API_EWB_MANAGE_ACCOUNT_INFO = '/manageAccountInfo';

export const LINKS_IMAGES =
  'https://www.eyefinity.com/products/solutions/ewebextra/template-images.html';
export const LINKS_WEBMAIL = 'https://webmail.eyefinity.com/';
export const LINKS_TEMPLATE =
  'https://www.eyefinity.com/products/solutions/ewebextra/new-website-design.html';
export const LINKS_TRAINING =
  'https://www.eyefinity.com/products/solutions/ewebextra/documentation.html';
export const LINKS_FAQ = 'https://www.eyefinity.com/resource/support/faqs.html';

export const LINKS_PE_HELP =
  'https://help.eyefinity.com/epm/Content/Patient_Engagement/Using_Eyefinity_Patient_Engagement_Solutions.htm';

/**
 * OauthService constants
 */
export const PECONN_EXPIRATION_TIME = 1799;
export const REFRESH_TIME_ALLOWANCE = 0.9;

export const ELOQUA_IFRAME_READY = 'ready';

/* Local storage key names*/
export const PECONN_TOKEN = 'PECONN';
export const EPM_EFAN = 'EFAN';
export const ADMIN = 'ADMIN';
export const LOGIN_KEY = 'LOGIN_KEY';

export const CLIENT_ID_QUERY_PARAM = 'clientId';
export const ACCESS_KEY_QUERY_PARAM = 'accessKey';
export const REDIRECT_FEATURE = 'feature';

export const CONVERT_SECONDS_TO_MS = 1000;
export const CACHED_COMPANY = 'cached_company';
export const CACHED_LOCATION = 'cached_location';

/* Export for ngx-mask to present options?  Not sure if this the right place for this yet*/
export const options: Partial<NgxMaskConfig> | (() => Partial<NgxMaskConfig>) =
  null;

/* Support Constants */
export const SUPPORT_INVALID_DOMAIN =
  'Invalid domain: Please enter a valid domain without http. ex. eyeglasses.com';
export const SUPPORT_INVALID_EFAN =
  'Invalid efan: Please enter a valid efan that must be exactly 10 digits. (ex. 1234567890)';
export const SUPPORT_INVALID_ENCOMPASS_COMPANY_ID =
  'Invalid Encompass Company ID: Please enter a valid ID that is not longer than 20 characters';
export const SUPPORT_INVALID_EMPTY_VALUE = 'Value cannot be empty';
export const SUPPORT_NO_PRACTICE_FOUND =
  'No company with provided criteria was found.';
/* RFV Constants*/
export const RFV_LOAD_LOCATION_ID_KEY = 'load_location';

/* Feature Toggle Constants*/
export const COMPANY_WIDE_FEATURE_TOGGLE_ID = 0;
export const FEATURE_TOGGLE_GENERIC_ERROR_MESSAGE =
  'There was an error while trying to save your feature toggles.';

/*  Reason for visit constants*/
export const REASON_FOR_VISIT_GENERIC_ERROR_MESSAGE =
  'There was an error while trying to save your reasons for visit.';
export const SUCCESSFUL_SAVE_NOTIFICATION_MESSAGE =
  'Save successful, allow up to 24 hours for changes to be visible.';

/* Verification Contact Information Constants*/
export const APPLY_COMPANY_WIDE_CONTACT_INFORMATION = -1;
export const NO_OP = -2;

/* EPN Constants*/
export const EPN_SIGNUP_PAYMENT_CONFIRMATION_MESSAGE =
  'Signing up for Electronic Patient Notifications costs $99 per month for the first location and $49 per month for each additional location.  Are you sure you want to sign up for EPN?';
export const DEFAULT_HOUR_TO_RUN = '11';
export const DEFAULT_DAY_OF = DayOfTypeEnum.WEDNESDAY;
export const DEFAULT_WEEK_OF = WeekOfTypeEnum.FIRST;
export const DEFAULT_TIME_BEFORE = TimeBeforeTypeEnum.MONTHLY;
export const DEFAULT_START_TIME = 6;
export const DEFAULT_END_TIME = 18;
export const SERVER_TIMEZONE = 'America/Los_Angeles';
export const SILLY_API_TIMEZONE_NAME = 'US/Pacific-New';

export const EPN_MESSAGE_PROPERTY_FILTER = 'uuid';

/*Message Freemarker Configuration Constants*/
export const FREEMARKER_FIRST_NAME = '${reminder.patientFirstName}';
export const FREEMARKER_PRACTICE_NAME = '${reminder.practice.name}';
export const FREEMARKER_PROVIDER_LAST_NAME = '${reminder.providerLastName}';
export const FREEMARKER_DOMAIN_NAME = '${reminder.practice.domainName}';
export const FREEMARKER_APPT_DATE = '${appointmentDate}';
export const FREEMARKER_APPT_TIME = '${startTime}';
export const FREEMARKER_ORDER_NOTIFICATION_PRACTICE_NAME = '${ord.pra}';
export const FREEMARKER_ORDER_NOTIFICATION_FIRST_NAME = '${ord.fir}';
export const FREEMARKER_ORDER_NOTIFICATION_DOMAIN = '${ord.dom}';
export const FREEMARKER_PHONE_NUMBER = '$PHONE$';
export const FREEMARKER_ADDRESS = '$ADDRESS$';

export function AMPM(): DropdownValueObject[] {
  return [
    { label: 'A M', value: 'AM' },
    { label: 'P M', value: 'PM' },
  ];
}

export function HOUR_TO_RUN_CHOICES(): DropdownValueObject[] {
  return [
    { label: HourToRunEnum.THREE_AM, value: 3 },
    { label: HourToRunEnum.FOUR_AM, value: 4 },
    { label: HourToRunEnum.FIVE_AM, value: 5 },
    { label: HourToRunEnum.SIX_AM, value: 6 },
    { label: HourToRunEnum.SEVEN_AM, value: 7 },
    { label: HourToRunEnum.EIGHT_AM, value: 8 },
    { label: HourToRunEnum.NINE_AM, value: 9 },
    { label: HourToRunEnum.TEN_AM, value: 10 },
    { label: HourToRunEnum.ELEVEN_AM, value: 11 },
    { label: HourToRunEnum.NOON, value: 12 },
    { label: HourToRunEnum.ONE_PM, value: 13 },
    { label: HourToRunEnum.TWO_PM, value: 14 },
    { label: HourToRunEnum.THREE_PM, value: 15 },
    { label: HourToRunEnum.FOUR_PM, value: 16 },
    { label: HourToRunEnum.FIVE_PM, value: 17 },
    { label: HourToRunEnum.SIX_PM, value: 18 },
    { label: HourToRunEnum.SEVEN_PM, value: 19 },
    { label: HourToRunEnum.EIGHT_PM, value: 20 },
    { label: HourToRunEnum.NINE_PM, value: 21 },
  ];
}

export function BUSINESS_TYPE(): DropdownValueObject[] {
  return [
    new DropdownValueObject(null, '<Select>'),
    ...Object.values(BusinessRegistrationInfo.BusinessTypeEnum).map(
      (v) => new DropdownValueObject(v),
    ),
  ];
}

export function BUSINESS_REGISTRATION_AUTHORITY(): DropdownValueObject[] {
  return [
    new DropdownValueObject(null, '<Select>'),
    ...Object.values(
      BusinessRegistrationInfo.BusinessRegistrationAuthorityEnum,
    ).map((v) => new DropdownValueObject(v)),
  ];
}

export function FREQUENCY(): DropdownValueObject[] {
  return [
    { label: TimeBeforeTypeEnum.MONTHLY, value: TimeBeforeTypeEnum.MONTHLY },
    { label: TimeBeforeTypeEnum.WEEKLY, value: TimeBeforeTypeEnum.WEEKLY },
  ];
}

export function TIME(): DropdownValueObject[] {
  const time: DropdownValueObject[] = [];
  for (let i = 1; i < 12; i++) {
    time[i - 1] = { value: i.toString(), label: i.toString() };
  }
  time[11] = { value: '0', label: '12' };
  return time;
}

export function DAY_OF(): DropdownValueObject[] {
  const dayOfs: DropdownValueObject[] = [];
  Object.keys(DayOfTypeEnum)
    .filter((key) => !isNaN(Number(DayOfTypeEnum[key])))
    .forEach((value) => {
      dayOfs.push({ label: value, value: DayOfTypeEnum[value] });
    });
  return dayOfs;
}

export function PROVIDER_ROLE_TYPES(): DropdownValueObject[] {
  const values = [];
  values.push(
    new DropdownValueObject(
      EwxProviderType.PROVIDER,
      EwxProviderType[EwxProviderType.PROVIDER],
    ),
  );
  values.push(
    new DropdownValueObject(
      EwxProviderType.STAFF,
      EwxProviderType[EwxProviderType.STAFF],
    ),
  );
  return values;
}

export function searchFields(): DropdownValueObject[] {
  return [
    ...Object.values(CompanySearchRequest.SearchFieldEnum).map(
      (v) => new DropdownValueObject(v, v.toString()),
    ),
  ];
}

export function isEmptyObject(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') {
    throw Error(`The argument is not an object: ${JSON.stringify(obj)}`);
  }
  return Object.keys(obj).length === 0;
}

export function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

export function WEEK_OF(): DropdownValueObject[] {
  const weekOfs: DropdownValueObject[] = [];
  Object.keys(WeekOfTypeEnum)
    .filter((key) => !isNaN(Number(WeekOfTypeEnum[key])))
    .forEach((value) => {
      weekOfs.push({ label: value, value: WeekOfTypeEnum[value] });
    });
  return weekOfs;
}

export function REASONS_FOR_VISIT(): DropdownValueObject[] {
  return [];
}

export interface RFVComponentState {
  activeMappingArray: EpmReasonForVisit[];
}

export interface RFVDuplicate {
  reasonDescriptionId: number;
}

export interface RFVSearchResult {
  reasons: EpmReasonForVisit[];
  total: number;
}

export interface RFVTableState {
  filterActive: boolean;
  searchTerm: string;
  sortColumn: ConnSortColumn;
  sortDirection: ConnSortDirection;
}

export interface RFVServiceState {
  practiceId: number;
  epmLocationId: string;
  appointmentTypes: EwxReasonForVisitCommand;
}

export class MessageConfigurationWrapper {
  contentGroupType: ContentGroupTypeEnum;
  dropdownData: DropdownValueObject[];
}

export class EwebSignupComponentWrapper {
  constructor(
    public schedulerToggle = false,
    public formsToggle = false,
    public email = new InputComponentWrapper(),
    public practiceName = new InputComponentWrapper(),
    public domainName = new InputComponentWrapper(),
    public isValid = false,
  ) {}
}

export class EwebManagementWrapper {
  constructor(
    public schedulerOn = false,
    public formsOn = false,
    public notificationEmailAddresses: string[] = [''],
    public efan = '',
    public domainName = '',
    public practiceName = '',
    public isOmiLinkActive = false,
    public primaryPhoneNumber = '',
    public practiceId = '',
    public epmLocations: AdminOffice[] = [],
  ) {}
}

export class EwebEditEfanDomainWrapper {
  constructor(
    public domainName: string,
    public newDomainName: string,
    public efan: string,
    public newEfan: string,
  ) {}
}

export class EmailInputBuilderWrapper {
  constructor(
    public emailCollection = [new InputComponentWrapper()],
    public isValid = false,
  ) {}
}

export class RemindersBuilderWrapper {
  constructor(
    public remindersCollection = [new MultiMessageConfig()],
    public isValid = false,
  ) {}
}

export class EwebIntegrationComponentWrapper {
  constructor(
    public schedulerToggle = false,
    public formsToggle = false,
    public emailBuilder = new EmailInputBuilderWrapper(),
    public primaryPhoneNumber: InputComponentWrapper = new InputComponentWrapper(),
    public isValid = false,
  ) {}
}

export class InputComponentWrapper {
  constructor(
    public inputText = '',
    public isValid = false,
  ) {}
}

export interface ContactInformationConfigurationWrapper {
  locationContactInformation: VerificationContactInfo;
  index: number;
}

export class PeConnAccessToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  application: string;
  companyId: string;
  efan: string;
  clientId: string;
  location: string;
  principal: string;
  signupStatusEpn: PeSignupState;
  signupStatusEwx: PeSignupState;
}

export interface ValidationErrorWrapper {
  isValid: boolean;
  errorMessage: string;
}

export interface DeactivatableComponent {
  canBeDeactivated(): boolean;
}

export interface MessageRunWrapper {
  messagingRuns: MessagingRunObject[];
}

export interface IncomingQueryParameters {
  currentUsername: string;
  currentCompanyId: string;
}

export interface LocationDataWrapper {
  index: number;
  data: LocationData;
}

export type FeatureToggleType = keyof PeFeatureToggleDto;

export interface FeatureToggleWrapper {
  index: number;
  data: PeFeatureToggleDto;
}

export interface DropdownValueMap {
  possibleValues: DropdownValueObject[];
  selectedValue: DropdownValueObject;
}

export interface NavGuardRedirectWrapper {
  path: string;
  queryParams: Params;
}

export interface SearchParameters {
  token: string;
  alan: string;
}

export type EwebStateChange = boolean | InputComponentWrapper;
